import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit
} from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import * as FileSaver from 'file-saver';
import { DateTime } from 'luxon';
import { Observable, Subscription, forkJoin, from } from 'rxjs';
import { OwnerFindingForm } from 'src/app/classes/form-groups/owner-finding-form';
import { OwnerForm } from 'src/app/classes/form-groups/owner-form';
import { CompanyVendor, Note, Vendor } from 'src/app/classes/models';
import { ComplianceSetting } from 'src/app/classes/models/compliance-setting';
import { Owner } from 'src/app/classes/models/owner';
import { ViveProgress } from 'src/app/classes/models/vive-progress';
import { ComplianceReviewAction } from 'src/app/interfaces/compliance-review-action';
import { OwnerFindingComplianceResult } from 'src/app/interfaces/compliance/owner-finding-compliance-result';
import { LocalizationInfo } from 'src/app/interfaces/localization-info';
import { AddEditOwnerModalComponent } from 'src/app/modals/add-edit-owner-modal/add-edit-owner-modal.component';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { NoteModalComponent } from 'src/app/modals/note-modal/note-modal.component';
import { AuthService } from 'src/app/services/auth.service';
import { CompanyVendorService } from 'src/app/services/company-vendor.service';
import { ComplianceStepStatusService } from 'src/app/services/compliance-step-status.service';
import { ComplianceService } from 'src/app/services/compliance.service';
import { OwnerService } from 'src/app/services/owner.service';
import { ReportService } from 'src/app/services/report.service';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';
import { VendorService } from 'src/app/services/vendor.service';

@Component({
  selector: 'app-compliance-owner-review',
  templateUrl: './compliance-owner-review.component.html',
  styleUrls: ['./compliance-owner-review.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ComplianceOwnerReviewComponent implements OnInit {
  constructor(
    private ToastService: ToastService,
    private CompanyVendorService: CompanyVendorService,
    private VendorService: VendorService,
    private route: ActivatedRoute,
    private SharedDataService: SharedDataService,
    private ComplianceService: ComplianceService,
    private UtilityService: UtilityService,
    private OwnerService: OwnerService,
    private ModalService: NgbModal,
    private ReportService: ReportService,
    private AuthService: AuthService,
    private ComplianceStepStatusService: ComplianceStepStatusService,
    private cdr: ChangeDetectorRef
  ) {
    const { UserAccountRole: { role } = {} } = this.LoggedInUser;

    if (role && this.ownerSSNPermissions.includes(role)) {
      this.hasOwnerSSNAccess = true;
    }
  }

  CompanyId!: number;
  VendorId!: number;
  CompanyVendorId!: number;
  CompanyComplianceTypeId!: number;
  action!: ComplianceReviewAction;
  vendorRiskLevel!: number;
  country!: 'US' | 'CA';

  // we capture an instance of the Auto Approve modal to ensure we only display 1 at a time.
  AutoApproveModal?: NgbModalRef;

  Vendor!: Vendor;
  CompanyVendor!: CompanyVendor;
  Owners: Owner[] = [];
  ComplianceProgress?: ViveProgress | null;
  ComplianceSetting!: ComplianceSetting;
  LocalizationInfo!: LocalizationInfo;
  // Maps Owner Finding results by Owner Id.
  MappedOwnerComplianceResults: Map<number, OwnerFindingComplianceResult> =
    new Map([]);
  // Maps Owner Form by Owner Id.
  MappedOwnerForms: Map<number, OwnerForm> = new Map<number, OwnerForm>([]);
  // Maps Owners by Owner Id.
  MappedOwners: Map<number, Owner> = new Map<number, Owner>([]);
  OwnerFormArray = new FormArray<OwnerForm>([]);

  SharedDataServiceSubscription!: Subscription;

  YesNoValues: [
    { label: ''; value: null },
    { label: 'No'; value: false },
    { label: 'Yes'; value: true }
  ] = [
    { label: '', value: null },
    { label: 'No', value: false },
    { label: 'Yes', value: true }
  ];

  FelonyYearValues: { label: string; value: number | null }[] = new Array(12)
    .fill(0)
    .map((value, index) => {
      if (index === 0) {
        return { label: '', value: null };
      } else if (index === 1) {
        return { label: 'No Felonies', value: index - 1 };
      } else {
        return {
          label: `${index - 1} Year${index === 2 ? '' : 's'}`,
          value: index - 1
        };
      }
    });

  MisdemeanorYearValues: { label: string; value: number | null }[] = new Array(
    12
  )
    .fill(0)
    .map((value, index) => {
      if (index === 0) {
        return { label: '', value: null };
      } else if (index === 1) {
        return { label: 'No Misdemeanors', value: index - 1 };
      } else {
        return {
          label: `${index - 1} Year${index === 2 ? '' : 's'}`,
          value: index - 1
        };
      }
    });

  BankruptcyValues: { label: string; value: number | null }[] = [
    { label: '', value: null },
    { label: 'No Bankruptcies', value: 0 },
    // { label: 'Forgiven Bankruptcies', value: -1 },
    { label: '0 - 1 Year Post Decisions', value: 1 },
    { label: '2 Years Post Decisions', value: 2 },
    { label: '3 Years Post Decisions', value: 3 },
    { label: '4 Years Post Decisions', value: 4 },
    { label: '5 Years Post Decisions', value: 5 },
    { label: '6 Years Post Decisions', value: 6 },
    { label: '7 Years Post Decisions', value: 7 },
    { label: '8+ Years Post Decisions', value: 8 }
  ];

  OwnerIdentityValues: [
    { label: ''; value: null },
    { label: 'No'; value: 0 },
    // { label: 'No', value: 10 },
    // { label: 'No', value: 20 },
    // { label: 'Yes', value: 30 },
    // { label: 'Yes', value: 40 },
    { label: 'Yes'; value: 50 }
  ] = [
    { label: '', value: null },
    { label: 'No', value: 0 },
    // { label: 'No', value: 10 },
    // { label: 'No', value: 20 },
    // { label: 'Yes', value: 30 },
    // { label: 'Yes', value: 40 },
    { label: 'Yes', value: 50 }
  ];

  OwnerWatchlistValues: [
    { label: ''; value: null },
    { label: 'No Watchlists'; value: 0 },
    { label: '1 Watchlist'; value: 1 },
    { label: 'Multiple Watchlists'; value: 2 }
  ] = [
    { label: '', value: null },
    { label: 'No Watchlists', value: 0 },
    { label: '1 Watchlist', value: 1 },
    { label: 'Multiple Watchlists', value: 2 }
  ];

  LoggedInUser = this.AuthService.getUser();

  ownerSSNPermissions = [
    'ADMIN_ADMIN',
    'ADMIN_FRAUD_TEAM',
    'ADMIN_PROJECT_MANAGEMENT_TEAM'
  ];

  hasOwnerSSNAccess = false;

  revealedOwnerSSNs: Map<number, string> = new Map();

  viewOwnerSSN(owner: Owner): void {
    if (!this.hasOwnerSSNAccess || !owner.id) {
      return;
    }

    this.OwnerService.getOwnerSSN({
      OwnerId: owner.id,
      VendorId: this.VendorId
    }).subscribe({
      next: (response) => {
        this.revealedOwnerSSNs.set(owner.id!, response.ssn);
        this.cdr.detectChanges();
      }
    });
  }

  getRevealedOwnerSSN(ownerId: number | undefined): string | undefined {
    return ownerId ? this.revealedOwnerSSNs.get(ownerId) : undefined;
  }

  isReportElevenMonthsOldOrOlder(lastRanReportDate: Date) {
    return (
      DateTime.fromJSDate(new Date()).diff(
        DateTime.fromJSDate(lastRanReportDate),
        'months'
      ).months >= 11
    );
  }

  checkForPassedFindings() {
    if (!this.CompanyVendor) {
      return false;
    }

    const { needsFeeWaiveRenewal } = this.CompanyVendor;

    if (needsFeeWaiveRenewal) {
      return false;
    }

    const { companyType } = this.Vendor;

    // VPDW-4272: Sole Proprietorship is the only case that can pass with no owners.
    // Otherwise `[].every()` is true, so an empty findings list auto-approved the
    // Owner Info step when the vendor never added any owners.
    if (companyType !== 'Sole Proprietorship' && this.Owners.length === 0) {
      return false;
    }

    const OwnerComplianceResults = Array.from(
      this.MappedOwnerComplianceResults.values()
    );

    return (
      companyType === 'Sole Proprietorship' ||
      OwnerComplianceResults.every((OwnerComplianceResult) => {
        return Object.values(OwnerComplianceResult).every((result) => {
          return ['NOT REQUIRED', 'PASSED'].includes(result);
        });
      })
    );
  }

  displayAutoApproveModal() {
    if (this.LoggedInUser && this.CompanyVendor) {
      const { UserAccountRole: { role } = {} } = this.LoggedInUser;
      const { viveStatus } = this.CompanyVendor;
      const { companyType } = this.Vendor;

      if (
        role &&
        new Set([
          'ADMIN_ADMIN',
          'ADMIN_FRAUD_TEAM',
          'ADMIN_PROJECT_MANAGEMENT_TEAM'
        ]).has(role) &&
        this.checkForPassedFindings() &&
        (this.ComplianceProgress === null ||
          (this.ComplianceProgress &&
            !['Passed', 'Failed'].includes(this.ComplianceProgress.status!))) &&
        viveStatus !== 'Inactive' &&
        !this.AutoApproveModal
      ) {
        this.AutoApproveModal = this.ModalService.open(ConfirmModalComponent, {
          backdrop: 'static',
          modalDialogClass: 'confirm-modal'
        });
        document.body.style.overflowY = 'auto';
        Object.assign(this.AutoApproveModal.componentInstance, {
          // VPDW-4199: scoped to this step, and names any sibling step still needing
          // attention so this never reads as a review-wide all-clear.
          displayText: this.ComplianceStepStatusService.buildStepApprovalText(
            'Owner Info',
            companyType === 'Sole Proprietorship'
              ? 'This Vendor is a Sole Proprietorship'
              : undefined
          )
        });

        const { result } = this.AutoApproveModal;

        from(result).subscribe({
          next: (result) => {
            if (result) {
              delete this.AutoApproveModal;
              this.SharedDataService.sendEvent(
                'UpdateComplianceProgress',
                new ViveProgress({ status: 'Passed', type: 'Owner Info' })
              );
              this.SharedDataService.sendEvent('GoToNextStep');
            }
          }
        });
      }
    }
  }

  recalculateOwnerComplianceResults(ComplianceResults: any) {
    this.UtilityService.constructObjectFromObjectProperties<{
      ownerFindingResults: OwnerFindingComplianceResult[];
    }>(ComplianceResults, ['ownerFindingResults']).ownerFindingResults.forEach(
      (OwnerFindingResult) => {
        const { OwnerId } = OwnerFindingResult;

        if (!OwnerId) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing Owner Id from results.',
            { returnError: true }
          );
        }

        delete OwnerFindingResult.OwnerId;
        this.MappedOwnerComplianceResults.set(OwnerId, OwnerFindingResult);
        this.displayAutoApproveModal();
      }
    );
  }

  getOwnerComplianceResult(
    OwnerFormGroup: OwnerForm,
    result:
      | 'ownerIdentity'
      | 'ownerWatchlist'
      | 'ownerFelony'
      | 'ownerMisdemeanor'
      | 'ownerBankruptcy'
      | 'ownerLiens'
      | 'ownerSexOffender'
  ) {
    const OwnerComplianceResults = this.MappedOwnerComplianceResults.get(
      OwnerFormGroup.controls['id'].value!
    );

    if (!OwnerComplianceResults) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There was an error retrieving compliance results.',
        { returnError: true }
      );
    }

    return OwnerComplianceResults[result];
  }

  getBankruptcyRequirementLabel() {
    const { ownerBankruptcy } = this.ComplianceSetting;

    if (ownerBankruptcy === undefined) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There was an error retrieving bankruptcy requirement.',
        { returnError: true }
      );
    }

    if (ownerBankruptcy === null) {
      return 'Not Required';
    } else if (ownerBankruptcy === 0) {
      return 'None Allowed';
    } else {
      return `${ownerBankruptcy} YPD`;
    }
  }

  getLiensDisplayText() {
    const { ownerLiens } = this.ComplianceSetting;

    if (ownerLiens === undefined) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There was an error retrieving liens & judgements requirement.',
        { returnError: true }
      );
    }

    if (ownerLiens === null) {
      return 'Not Required';
    } else {
      return `${this.LocalizationInfo.moneySymbol.label}${ownerLiens}`;
    }
  }

  // Gets requirement display text for misdemeanor or felony.
  getMisdemeanorOrFelonyDisplayText(settingValue: number | null | undefined) {
    if (settingValue === undefined) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There was an error retrieving misdemeanor or felony requirement.',
        { returnError: true }
      );
    }

    if (settingValue === null) {
      return 'Not Required';
    } else if (settingValue === 0) {
      return 'None Allowed';
    } else {
      return `${settingValue} Year${settingValue === 1 ? '' : 's'}`;
    }
  }

  getOwnerWatchlistDisplayText() {
    const { ownerWatchlist } = this.ComplianceSetting;

    if (ownerWatchlist === undefined) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There was an error retrieving watchlist requirement.',
        { returnError: true }
      );
    }

    if (ownerWatchlist === 0) {
      return 'No Watchlists';
    } else if (ownerWatchlist === 1) {
      return '1 Watchlist';
    } else if (typeof ownerWatchlist === 'number' && ownerWatchlist! > 1) {
      return 'Multiple Watchlists';
    } else {
      return 'Not Required';
    }
  }

  addOwner() {
    const AddEditOwnerModal = this.ModalService.open(
      AddEditOwnerModalComponent,
      { backdrop: 'static', modalDialogClass: 'add-edit-owner-modal' }
    );
    const { componentInstance, result } = AddEditOwnerModal;
    Object.assign(componentInstance, {
      VendorId: this.VendorId,
      country: this.country
    });

    from(result).subscribe({
      next: (AddedOwner: Owner) => {
        const { id: OwnerId } = AddedOwner;

        if (!OwnerId) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Owner is missing id.',
            { returnError: true }
          );
        }

        this.MappedOwners.set(OwnerId, AddedOwner);
        const OwnerFormGroup = new OwnerForm();
        OwnerFormGroup.setControl(
          'OwnerFinding',
          new FormGroup(new OwnerFindingForm())
        );
        OwnerFormGroup.patchValue(AddedOwner);
        OwnerFormGroup.controls['OwnerFinding']?.patchValue({
          VendorId: this.VendorId,
          OwnerId
        });
        this.OwnerFormArray.push(OwnerFormGroup);
        this.MappedOwnerForms.set(OwnerId, OwnerFormGroup);
        this.Owners.push(AddedOwner);

        const OwnerFindingResult: OwnerFindingComplianceResult = {
          OwnerId,
          ownerBankruptcy: 'MISSING',
          ownerFelony: 'MISSING',
          ownerIdentity: 'MISSING',
          ownerLiens: 'MISSING',
          ownerMisdemeanor: 'MISSING',
          ownerSexOffender: 'MISSING',
          ownerWatchlist: 'MISSING'
        };
        this.MappedOwnerComplianceResults.set(OwnerId, OwnerFindingResult);
      }
    });
  }

  editOwner(Owner: Owner) {
    const { id: OwnerId } = Owner;

    if (!OwnerId) {
      throw this.ToastService.addErrorToast('Error', 'Owner is missing id.', {
        returnError: true
      });
    }

    const OwnerForm = this.MappedOwnerForms.get(OwnerId);

    if (!OwnerForm) {
      throw this.ToastService.addErrorToast('Error', 'Owner Form is missing.', {
        returnError: true
      });
    }

    const AddEditOwnerModal = this.ModalService.open(
      AddEditOwnerModalComponent,
      { backdrop: 'static', modalDialogClass: 'add-edit-owner-modal' }
    );
    const { componentInstance, result } = AddEditOwnerModal;
    Object.assign(componentInstance, {
      Owner,
      VendorId: this.VendorId,
      country: this.country,
      revealedOwnerSSN: this.revealedOwnerSSNs.get(OwnerId)
    });

    componentInstance.ownerSSNRevealedEvent.subscribe((ssn: string) => {
      this.revealedOwnerSSNs.set(OwnerId, ssn);
      this.cdr.detectChanges();
    });

    from(result).subscribe({
      next: (UpdatedOwner: Owner) => {
        Object.assign(Owner, UpdatedOwner);
        OwnerForm.patchValue(UpdatedOwner);
      }
    });
  }

  deleteOwner(Owner: Owner) {
    const { id: OwnerId } = Owner;

    if (!OwnerId) {
      throw this.ToastService.addErrorToast('Error', 'Owner is missing id.', {
        returnError: true
      });
    }

    const ConfirmModal = this.ModalService.open(ConfirmModalComponent, {
      backdrop: 'static',
      modalDialogClass: 'confirm-modal'
    });
    const { componentInstance, result } = ConfirmModal;
    Object.assign(componentInstance, {
      displayText: 'Are you sure you want to delete this Owner?'
    });

    from(result).subscribe({
      next: (confirm) => {
        if (confirm) {
          this.OwnerService.deleteOwner(OwnerId, this.VendorId).subscribe({
            next: () => {
              this.Owners.splice(
                this.Owners.findIndex((o) => o === Owner),
                1
              );
              this.constructOwnerFormGroups(structuredClone(this.Owners));
              throw this.ToastService.addSuccessToast(
                'Error',
                'Owner was successfully deleted.'
              );
            }
          });
        }
      }
    });
  }

  // Display note modal to override findings.
  overrideOwnerFindings(OwnerFormGroup: OwnerForm) {
    const NoteModal = this.ModalService.open(NoteModalComponent, {
      backdrop: 'static',
      modalDialogClass: 'note-modal'
    });
    const { componentInstance } = NoteModal;

    const titleOptions: string[] = [
      'Owner Information-Override',
      'Owner Information- Edits',
      'Owner Information - Waive/Cure'
    ];

    Object.assign(componentInstance, {
      CompanyId: this.CompanyId,
      VendorId: this.VendorId,
      titleOptions
    });

    from(NoteModal.result).subscribe({
      next: ({ Note }: { Note: Note }) => {
        const OwnerFindingControl = OwnerFormGroup.controls['OwnerFinding'];
        if (!OwnerFindingControl) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Owner finding control is missing from form.',
            { returnError: true }
          );
        }
        const { value: OwnerFinding } = OwnerFindingControl;
        this.ComplianceService.overrideOwnerFinding(this.VendorId, {
          OwnerFinding,
          Note
        }).subscribe({
          next: ({ Note, OwnerFinding }) => {
            OwnerFormGroup.patchValue({ OwnerFinding });

            // Recalculate Owner Compliance Results on override and reapply the results.
            this.ComplianceService.getComplianceResults(
              this.CompanyId,
              this.VendorId
            ).subscribe({
              next: (ComplianceResults) => {
                this.recalculateOwnerComplianceResults(ComplianceResults);
              }
            });

            this.ToastService.addSuccessToast(
              'Success!',
              'Successfully overrided Owner Findings.'
            );
          }
        });
      }
    });
  }

  downloadRawDataReports(OwnerForm: OwnerForm) {
    const OwnerId = OwnerForm.controls['id'].value;

    if (!OwnerId) {
      throw this.ToastService.addErrorToast('Error', 'Missing Owner Id.', {
        returnError: true
      });
    }

    forkJoin([
      this.ReportService.getLexNexReport(
        this.UtilityService.stringifyObjectsInQueryObject({
          options: { limit: 1 },
          VendorId: this.VendorId,
          OwnerId,
          reqType: 'BpsSearch',
          order: [['createdAt', 'DESC']]
        })
      ),
      this.ReportService.getLexNexReport(
        this.UtilityService.stringifyObjectsInQueryObject({
          options: { limit: 1 },
          VendorId: this.VendorId,
          OwnerId,
          reqType: 'BpsReport',
          order: [['createdAt', 'DESC']]
        })
      )
    ]).subscribe({
      next: ([LexNexOwnerSearch, LexNexOwnerReport]) => {
        if (LexNexOwnerSearch) {
          const { resJson } = LexNexOwnerSearch;
          const formattedReportData = JSON.stringify(resJson, null, 2);
          FileSaver.saveAs(
            new Blob([formattedReportData], { type: 'text/plain' }),
            `${this.Vendor.legalName}-LexNexOwnerSearchRawData.txt`
          );
        }
        if (LexNexOwnerReport) {
          const { resJson } = LexNexOwnerReport;
          const formattedReportData = JSON.stringify(resJson, null, 2);
          FileSaver.saveAs(
            new Blob([formattedReportData], { type: 'text/plain' }),
            `${this.Vendor.legalName}-LexNexOwnerReportRawData.txt`
          );
        }
      }
    });
  }

  handleBusinessVerificationRun(
    Owner: Owner,
    ComplianceResults: any,
    displaySuccess: boolean = true
  ) {
    const { id: OwnerId } = Owner;

    if (!OwnerId) {
      throw this.ToastService.addErrorToast('Error', 'Missing Owner Id.', {
        returnError: true
      });
    }

    this.MappedOwners.set(OwnerId, Owner);
    const OwnerForm = this.MappedOwnerForms.get(OwnerId);
    OwnerForm?.patchValue(Owner);

    this.recalculateOwnerComplianceResults(ComplianceResults);

    if (displaySuccess) {
      this.ToastService.addSuccessToast(
        'Success',
        'Successfully ran Owner verification.'
      );
    }
  }

  runVerificationOnOwner(OwnerFormGroup: OwnerForm) {
    const { id: OwnerId, VendorId } = OwnerFormGroup.value;

    if (!OwnerId) {
      throw this.ToastService.addErrorToast('Error', 'Owner is missing id.', {
        returnError: true
      });
    }

    if (!VendorId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Owner is missing Vendor Id.',
        { returnError: true }
      );
    }

    this.ComplianceService.runVerificationOnOwner(
      { VendorId, OwnerId, CompanyId: this.CompanyId },
      { waitForJobs: true }
    ).subscribe({
      next: ([Owner, ComplianceResults]) => {
        this.handleBusinessVerificationRun(Owner, ComplianceResults);
      },
      error: () => {
        forkJoin([
          this.VendorService.getVendorV2(this.VendorId, {
            attributes: JSON.stringify(['id']),
            Owner: JSON.stringify({
              options: { required: false },
              id: OwnerId,
              aliasedAttributes: ['lastRanOwnerVerificationReportDate'],
              OwnerFinding: {},
              Address: {
                attributes: ['id', 'state', 'OwnerId'],
                options: { required: true }
              }
            })
          }),
          this.ComplianceService.getComplianceResults(
            this.CompanyId,
            this.VendorId
          )
        ]).subscribe({
          next: ([Vendor, ComplianceResults]) => {
            const { Owners } = Vendor;
            if (Owners?.length !== 1) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Missing Owner information.',
                { returnError: true }
              );
            }
            const Owner = Owners[0];
            this.handleBusinessVerificationRun(Owner, ComplianceResults, false);
          }
        });
      }
    });
  }

  runAllVerificationOnOwners() {
    const observables: Observable<[Owner, any]>[] = [];
    this.Owners.forEach((Owner) => {
      const { id: OwnerId, VendorId } = Owner;

      if (!OwnerId) {
        throw this.ToastService.addErrorToast('Error', 'Owner is missing id.', {
          returnError: true
        });
      }

      if (!VendorId) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Owner is missing Vendor Id.',
          { returnError: true }
        );
      }

      observables.push(
        this.ComplianceService.runVerificationOnOwner(
          { VendorId, OwnerId, CompanyId: this.CompanyId },
          { waitForJobs: true }
        )
      );
    });

    forkJoin(observables).subscribe({
      next: (responseArray) => {
        responseArray.forEach(([Owner, ComplianceResults]) => {
          this.handleBusinessVerificationRun(Owner, ComplianceResults);
        });
      },
      error: () => {
        forkJoin([
          this.VendorService.getVendorV2(this.VendorId, {
            attributes: JSON.stringify(['id']),
            Owner: JSON.stringify({
              options: { required: false },
              aliasedAttributes: ['lastRanOwnerVerificationReportDate'],
              OwnerFinding: {},
              Address: {
                attributes: ['id', 'state', 'OwnerId'],
                options: { required: true }
              }
            })
          }),
          this.ComplianceService.getComplianceResults(
            this.CompanyId,
            this.VendorId
          )
        ]).subscribe({
          next: ([Vendor, ComplianceResults]) => {
            const { Owners } = Vendor;

            if (!Owners) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Missing Owner information.',
                { returnError: true }
              );
            }

            // Ensure this works.
            Owners.forEach((Owner) => {
              this.handleBusinessVerificationRun(
                Owner,
                ComplianceResults,
                false
              );
            });
          }
        });
      }
    });
  }

  constructOwnerFormGroups(Owners: Owner[]) {
    this.OwnerFormArray.clear();
    this.Owners.splice(0);
    Owners.forEach((Owner) => {
      const { id: OwnerId } = Owner;

      if (!OwnerId) {
        throw this.ToastService.addErrorToast('Error', 'Owner is missing id.', {
          returnError: true
        });
      }

      this.MappedOwners.set(OwnerId, Owner);
      const OwnerFormGroup = new OwnerForm();
      OwnerFormGroup.setControl(
        'OwnerFinding',
        new FormGroup(new OwnerFindingForm())
      );
      OwnerFormGroup.patchValue(Owner);
      OwnerFormGroup.controls['OwnerFinding']?.patchValue({
        VendorId: this.VendorId,
        OwnerId
      });
      this.OwnerFormArray.push(OwnerFormGroup);
      this.MappedOwnerForms.set(OwnerId, OwnerFormGroup);
      this.Owners.push(Owner);
    });
  }

  ngOnDestroy() {
    // Need to unsubscribe from shared data service when component is destroyed to prevent
    // potential events from firing when next component is instantiated.
    if (this.SharedDataServiceSubscription) {
      this.SharedDataServiceSubscription.unsubscribe();
    }
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe({
      next: (paramMap) => {
        const CompanyId = Number(paramMap.get('CompanyId'));
        const VendorId = Number(paramMap.get('VendorId'));
        const CompanyVendorId = Number(paramMap.get('CompanyVendorId'));
        const CompanyComplianceTypeId = Number(
          paramMap.get('CompanyComplianceTypeId')
        );

        if (!CompanyId) {
          throw this.ToastService.addErrorToast('Error', 'Missing Client Id.', {
            returnError: true
          });
        }

        if (!VendorId) {
          throw this.ToastService.addErrorToast('Error', 'Missing Vendor Id.', {
            returnError: true
          });
        }

        if (!CompanyVendorId) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing Client/Vendor Id.',
            { returnError: true }
          );
        }

        if (!CompanyComplianceTypeId) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing Compliance Type Id.',
            { returnError: true }
          );
        }

        Object.assign(this, {
          CompanyId,
          VendorId,
          CompanyVendorId,
          CompanyComplianceTypeId
        });

        forkJoin([
          this.CompanyVendorService.getCompanyVendorCount({
            CompanyId: this.CompanyId,
            VendorId: this.VendorId,
            isArchived: true
          }),
          this.ComplianceService.getTierLevelForVendorV2(
            this.CompanyId,
            this.VendorId,
            this.CompanyComplianceTypeId
          )
        ]).subscribe({
          next: ([
            countOfArchivedClientVendorRelationships,
            vendorRiskLevel
          ]) => {
            const companyVendorQuery = {
              attributes: JSON.stringify(['id']),
              id: CompanyVendorId,
              Company: JSON.stringify({
                options: { required: true },
                attributes: ['id', 'country'],
                ComplianceSetting: {
                  attributes: [
                    'id',
                    'riskLevel',
                    'ownerIdentity',
                    'ownerWatchlist',
                    'ownerFelony',
                    'ownerMisdemeanor',
                    'ownerBankruptcy',
                    'ownerLiens',
                    'ownerSexOffender'
                  ],
                  options: { required: false },
                  CompanyComplianceTypeId: this.CompanyComplianceTypeId,
                  riskLevel: vendorRiskLevel
                }
              }),
              Vendor: JSON.stringify({
                options: { required: true },
                attributes: [
                  'id',
                  'legalName',
                  'companyType',
                  'hasComplexOwnership'
                ],
                aliasedAttributes: ['hasOwnerReportData'],
                Owner: {
                  options: { required: false },
                  aliasedAttributes: [
                    'lastRanOwnerVerificationReportDate',
                    'hasSSN'
                  ],
                  OwnerFinding: {},
                  Address: {
                    attributes: [
                      'id',
                      'address1',
                      'address2',
                      'city',
                      'state',
                      'zip',
                      'OwnerId'
                    ],
                    options: { required: true }
                  }
                }
              })
            };

            if (countOfArchivedClientVendorRelationships > 0) {
              Object.assign(companyVendorQuery, { isArchived: true });
            }

            forkJoin([
              this.CompanyVendorService.getCompanyVendor(companyVendorQuery),
              this.ComplianceService.getComplianceResults(
                this.CompanyId,
                this.VendorId
              )
            ]).subscribe({
              next: ([CompanyVendor, ComplianceResults]) => {
                if (!CompanyVendor) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Missing Client/Vendor information.',
                    { returnError: true }
                  );
                }

                this.CompanyVendor = CompanyVendor;

                const { Vendor, Company } = CompanyVendor;

                if (!Vendor) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Missing Vendor information.',
                    { returnError: true }
                  );
                }

                if (!Company) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Missing Client information.',
                    { returnError: true }
                  );
                }

                this.Vendor = Vendor;

                const { country, ComplianceSettings } = Company;

                if (
                  !Array.isArray(ComplianceSettings) ||
                  ComplianceSettings.length !== 1
                ) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Client is missing Compliance Settings.',
                    { returnError: true }
                  );
                }

                if (!country) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Missing country information.',
                    { returnError: true }
                  );
                }

                this.country = country;

                this.LocalizationInfo =
                  this.UtilityService.getLocalizedValuesMap(this.country);

                this.ComplianceSetting = ComplianceSettings[0];

                const { riskLevel } = this.ComplianceSetting;

                if (riskLevel === undefined) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Missing vendor risk level.',
                    { returnError: true }
                  );
                }

                this.vendorRiskLevel = riskLevel;

                const { companyType, Owners } = Vendor;

                // if (!Array.isArray(Owners) || Owners.length === 0) {
                //   throw this.ToastService.addErrorToast('Error', 'Vendor is missing Owner information.', { returnError: true });
                // }

                const OwnersThatNeedToRunVerification: Owner[] = [];

                if (Owners) {
                  this.constructOwnerFormGroups(Owners);

                  Owners.forEach((Owner) => {
                    const { lastRanOwnerVerificationReportDate } = Owner;

                    if (
                      !lastRanOwnerVerificationReportDate ||
                      this.isReportElevenMonthsOldOrOlder(
                        lastRanOwnerVerificationReportDate
                      )
                    ) {
                      OwnersThatNeedToRunVerification.push(Owner);
                    }
                  });
                }

                this.recalculateOwnerComplianceResults(ComplianceResults);

                this.SharedDataServiceSubscription =
                  this.SharedDataService.listenForEvent<
                    ViveProgress | null | ComplianceReviewAction
                  >().subscribe({
                    next: ({ eventName, data }) => {
                      if (
                        eventName === 'SetReviewAction' &&
                        data &&
                        !(data instanceof ViveProgress)
                      ) {
                        this.action = data;
                        this.SharedDataService.sendEvent(
                          'GetComplianceProgress',
                          new ViveProgress({ type: 'Owner Info' })
                        );
                      }

                      if (
                        eventName === 'SetComplianceProgress' &&
                        (data instanceof ViveProgress || data === null)
                      ) {
                        this.ComplianceProgress = data;

                        const needsToRunOwnerVerification = !!(
                          this.ComplianceProgress &&
                          this.ComplianceProgress.status === 'Reviewing' &&
                          OwnersThatNeedToRunVerification.length > 0 &&
                          companyType !== 'Sole Proprietorship'
                        );

                        if (!needsToRunOwnerVerification) {
                          this.displayAutoApproveModal();
                        }

                        // Set compliance progress if running compliance review.
                        if (this.action === 'review') {
                          if (
                            !this.ComplianceProgress ||
                            this.ComplianceProgress.status === null
                          ) {
                            this.SharedDataService.sendEvent(
                              'UpdateComplianceProgress',
                              new ViveProgress({
                                status: 'Reviewing',
                                type: 'Owner Info'
                              })
                            );
                          } else if (needsToRunOwnerVerification) {
                            // If the owner doesn't have a report or the owner's report is eleven months or older,
                            // run owner verification.
                            OwnersThatNeedToRunVerification.forEach((Owner) => {
                              const { id: OwnerId } = Owner;

                              if (!OwnerId) {
                                throw this.ToastService.addErrorToast(
                                  'Error',
                                  'Missing owner id.',
                                  { returnError: true }
                                );
                              }

                              const OwnerFormGroup =
                                this.MappedOwnerForms.get(OwnerId);

                              if (!OwnerFormGroup) {
                                throw this.ToastService.addErrorToast(
                                  'Error',
                                  'Missing mapped owner form.',
                                  { returnError: true }
                                );
                              }

                              this.runVerificationOnOwner(OwnerFormGroup);
                            });
                          }
                        }
                      }

                      if (eventName === 'CheckForPassedFindings') {
                        this.SharedDataService.sendEvent(
                          'RespondIfPassedFindings',
                          this.checkForPassedFindings()
                        );
                      }
                    }
                  });

                this.SharedDataService.sendEvent('GetReviewAction');
              }
            });
          }
        });
      }
    });
  }
}
